import { useEffect, useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { getMouvements, annulerMouvement } from '../lib/api'
import { getMouvementsCaissier } from '../lib/apiCaissier'

function timeAgo(dateStr, lang) {
  const diff = Date.now() - new Date(dateStr).getTime()
  if (diff < 60000) return lang === 'fr' ? "À l'instant" : 'Just now'
  if (diff < 3600000) return Math.floor(diff / 60000) + ' min'
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h'
  return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short' })
}

// Vérifie si un mouvement peut être annulé (moins de 24h et pas déjà une annulation)
function peutAnnuler(mouvement) {
  const diff = Date.now() - new Date(mouvement.created_at).getTime()
  const estAnnulation = mouvement.note?.startsWith('⚠ Annulation')
  return diff < 24 * 3600000 && !estAnnulation
}

export default function Historique({ boutiqueId: boutiqueIdProp }) {
  const { user, boutiqueId: boutiqueIdAuth } = useAuth()
  const { t, lang } = useLang()
  const isCaissier = !user && !!boutiqueIdProp
  const boutiqueId = boutiqueIdProp || boutiqueIdAuth

  const [mouvements, setMouvements] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtre, setFiltre] = useState('tous')
  const [confirmModal, setConfirmModal] = useState(null)
  const [annulation, setAnnulation] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    if (!boutiqueId) return
    load()
  }, [boutiqueId])

  async function load() {
    const fn = isCaissier
      ? getMouvementsCaissier(boutiqueId, 100)
      : getMouvements(boutiqueId, 100)
    const { data } = await fn
    setMouvements(data || [])
    setLoading(false)
  }

  async function handleAnnuler() {
    if (!confirmModal) return
    setAnnulation(true)
    const { error } = await annulerMouvement(boutiqueId, confirmModal)
    setAnnulation(false)
    setConfirmModal(null)
    if (error) {
      setMsg({ type: 'error', text: error.message })
    } else {
      setMsg({ type: 'success', text: lang === 'fr' ? '✓ Mouvement annulé avec succès !' : '✓ Movement cancelled successfully!' })
      load()
    }
    setTimeout(() => setMsg(null), 3000)
  }

  const filtered = mouvements.filter(m => filtre === 'tous' || m.type === filtre)
  const isAnnulationMvt = (m) => m.note?.startsWith('⚠ Annulation')

  return (
    <div className="main">
      <div className="page-title">{t('history')}</div>

      <div className="filter-tabs">
        {[['tous', t('all')], ['entree', t('entries')], ['sortie', t('exits')]].map(([val, label]) => (
          <button key={val} className={`filter-tab ${filtre === val ? 'active' : ''}`} onClick={() => setFiltre(val)}>
            {label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={msg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: '1rem' }}>
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="empty">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">📋</span>
          {t('no_history')}
        </div>
      ) : (
        filtered.map(m => {
          const estAnnulation = isAnnulationMvt(m)
          const prixAchat = m.prix_achat || 0
          const prixVente = m.prix_unitaire || 0
          const valeur = m.type === 'sortie' ? m.quantite * prixVente : m.quantite * prixAchat

          return (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '12px 0', borderBottom: '1px solid var(--cream-dark)',
              opacity: estAnnulation ? 0.6 : 1
            }}>
              {/* BADGE TYPE */}
              <div className={`hist-badge ${m.type === 'entree' ? 'hist-in' : 'hist-out'}`}
                style={{ opacity: estAnnulation ? 0.5 : 1 }}>
                {estAnnulation ? '↩' : m.type === 'entree' ? '↑' : '↓'}
              </div>

              {/* INFO */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="hist-name" style={{ textDecoration: estAnnulation ? 'line-through' : 'none' }}>
                  {m.produit_nom}
                </div>
                <div className="hist-time">
                  {estAnnulation
                    ? (lang === 'fr' ? '↩ Annulé' : '↩ Cancelled')
                    : (m.note || (m.type === 'entree' ? t('entry_label') : t('exit_label')))}
                  {' · '}{timeAgo(m.created_at, lang)}
                </div>
                {/* VALEUR */}
                {valeur > 0 && (
                  <div style={{ fontSize: 11, color: m.type === 'sortie' ? 'var(--sage)' : 'var(--muted)', marginTop: 2 }}>
                    {m.type === 'sortie'
                      ? `+${valeur.toLocaleString()} FCFA`
                      : `-${valeur.toLocaleString()} FCFA`}
                  </div>
                )}
              </div>

              {/* QUANTITÉ */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className={`hist-qty ${m.type === 'entree' ? 'in' : 'out'}`}>
                  {m.type === 'entree' ? '+' : '−'}{m.quantite}
                </div>
                {/* BOUTON ANNULER */}
                {!isCaissier && peutAnnuler(m) && (
                  <button
                    onClick={() => setConfirmModal(m)}
                    style={{
                      marginTop: 4, padding: '3px 8px', borderRadius: 50,
                      fontSize: 10, fontWeight: 500, cursor: 'pointer',
                      border: '1px solid #DFA0A0', background: 'var(--rose-pale)',
                      color: 'var(--rose)', fontFamily: 'DM Sans, sans-serif',
                      whiteSpace: 'nowrap'
                    }}>
                    ↩ {lang === 'fr' ? 'Annuler' : 'Undo'}
                  </button>
                )}
              </div>
            </div>
          )
        })
      )}

      {/* ── MODAL CONFIRMATION ANNULATION ── */}
      {confirmModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmModal(null)}>
          <div className="modal">
            <div className="modal-handle" />

            {/* ICÔNE */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>↩️</div>
              <div className="modal-title" style={{ marginBottom: 4 }}>
                {lang === 'fr' ? 'Annuler ce mouvement ?' : 'Cancel this movement?'}
              </div>
            </div>

            {/* DÉTAIL DU MOUVEMENT À ANNULER */}
            <div style={{
              background: 'var(--cream)', borderRadius: 'var(--radius-md)',
              padding: '12px 14px', marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--charcoal)' }}>
                  {confirmModal.produit_nom}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: confirmModal.type === 'entree' ? 'var(--sage)' : 'var(--rose)' }}>
                  {confirmModal.type === 'entree' ? '+' : '−'}{confirmModal.quantite}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                {confirmModal.type === 'entree' ? '📥' : '📤'} {confirmModal.note || (confirmModal.type === 'entree' ? t('entry_label') : t('exit_label'))}
                {' · '}{timeAgo(confirmModal.created_at, lang)}
              </div>
            </div>

            {/* EXPLICATION */}
            <div style={{
              background: 'var(--amber-pale)', border: '1px solid #E8C080',
              borderRadius: 'var(--radius-md)', padding: '10px 14px',
              fontSize: 12, color: '#633806', lineHeight: 1.7, marginBottom: '1.25rem'
            }}>
              ⚠️ {lang === 'fr'
                ? `Cette action va ${confirmModal.type === 'entree' ? 'retirer' : 'remettre'} ${confirmModal.quantite} unité(s) de "${confirmModal.produit_nom}" dans le stock. Un mouvement d'annulation sera enregistré dans l'historique.`
                : `This will ${confirmModal.type === 'entree' ? 'remove' : 'add back'} ${confirmModal.quantite} unit(s) of "${confirmModal.produit_nom}" in stock. A cancellation movement will be recorded in history.`}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setConfirmModal(null)}>
                {t('cancel')}
              </button>
              <button
                className="btn-confirm danger"
                onClick={handleAnnuler}
                disabled={annulation}
              >
                {annulation
                  ? (lang === 'fr' ? 'Annulation...' : 'Cancelling...')
                  : (lang === 'fr' ? '↩ Confirmer l\'annulation' : '↩ Confirm cancellation')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
